import { infiniteQueryOptions, mutationOptions } from "@tanstack/react-query";
import {
  ADD_BOOKMARK,
  ADD_FAV,
  DELETE_BOOKMARK,
  LIST_BOOKMARK,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { HAPTIC_STYLE, native } from "~/lib/nativeBridge";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { EVENT_NAMES, trackEvent } from "~/lib/tracking";
import type { UserProfile } from "~/types/userProfile";
import type {
  BookmarkListResponse,
  ToggleBookmarkVariables,
  ToggleFavoriteVariables,
} from "./types";

/** 1 ページの取得件数（skip/take によるページネーション） */
const BOOKMARK_PAGE_SIZE = 20;

/**
 * ブックマーク（☆ = UI 上の「お気に入り」）したユーザー一覧。
 * list_bookmark は last_login_time / video_call_waiting / voice_call_waiting も返すため
 * 補完取得は不要。skip/take の無限スクロールで全件たどれる（72 件上限なし）。
 */
export const bookmarkListInfiniteQueryOptions = infiniteQueryOptions({
  queryKey: queryKeys.bookmark.list(),
  queryFn: ({ pageParam }) =>
    apiClient<BookmarkListResponse>(LIST_BOOKMARK, {
      skip: pageParam,
      take: BOOKMARK_PAGE_SIZE,
    }),
  initialPageParam: 0,
  getNextPageParam: (lastPage, _allPages, lastPageParam) =>
    // 取得件数が take 未満なら最終ページ
    lastPage.length < BOOKMARK_PAGE_SIZE
      ? undefined
      : lastPageParam + BOOKMARK_PAGE_SIZE,
});

/** UserProfile キャッシュを楽観的に書き換え、ロールバック用にスナップショットを返す */
const optimisticUpdateProfile = async (
  userId: string,
  patch: Partial<UserProfile>,
) => {
  await queryClient.cancelQueries({
    queryKey: queryKeys.user.profile(userId),
  });
  const previous = queryClient.getQueryData<UserProfile>(
    queryKeys.user.profile(userId),
  );
  queryClient.setQueryData<UserProfile>(
    queryKeys.user.profile(userId),
    (old) => (old ? { ...old, ...patch } : old),
  );
  return previous;
};

const rollbackProfile = (userId: string, previous: UserProfile | undefined) => {
  if (previous) {
    queryClient.setQueryData(queryKeys.user.profile(userId), previous);
  }
};

/** いいね（ハート）追加 — 一方向。rmv_fav は API 側に存在しない */
export const addFavoriteMutationOptions = mutationOptions({
  mutationFn: (data: ToggleFavoriteVariables) =>
    apiClient<unknown>(ADD_FAV, {
      req_user_id: data.userId,
    }),
  onMutate: (variables) =>
    optimisticUpdateProfile(variables.userId, { is_fav: 1 }),
  onSuccess: () => {
    native.triggerHapticFeedback(HAPTIC_STYLE.NOTIFICATION_SUCCESS);
    trackEvent(EVENT_NAMES.SEND_GOOD);
  },
  onError: (_err, variables, previous) => {
    rollbackProfile(variables.userId, previous);
  },
  onSettled: (_data, _err, variables) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.profile(variables.userId),
    });
    // いいね一覧（設定 > いいね）の「自分から」を最新化。
    // received（相手から）は相手起因で自分の操作では変わらないが、
    // like.all で両タブまとめて無効化する（次回参照時に refetch）
    queryClient.invalidateQueries({ queryKey: queryKeys.like.all });
  },
});

/** ブックマーク（スター）追加 — Jambo API は partner_id を期待 */
export const addBookmarkMutationOptions = mutationOptions({
  mutationFn: (data: ToggleBookmarkVariables) =>
    apiClient<unknown>(ADD_BOOKMARK, {
      partner_id: data.userId,
    }),
  onMutate: (variables) =>
    optimisticUpdateProfile(variables.userId, { bookmark: true }),
  onSuccess: () => {
    native.triggerHapticFeedback(HAPTIC_STYLE.NOTIFICATION_SUCCESS);
    trackEvent(EVENT_NAMES.ADD_FAVORITE);
  },
  onError: (_err, variables, previous) => {
    rollbackProfile(variables.userId, previous);
  },
  onSettled: (_data, _err, variables) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.profile(variables.userId),
    });
    // ブックマーク一覧（マイページの「お気に入り」）を最新化
    queryClient.invalidateQueries({ queryKey: queryKeys.bookmark.list() });
  },
});

/** ブックマーク（スター）削除 — Jambo API は partner_id を期待 */
export const removeBookmarkMutationOptions = mutationOptions({
  mutationFn: (data: ToggleBookmarkVariables) =>
    apiClient<unknown>(DELETE_BOOKMARK, {
      partner_id: data.userId,
    }),
  onMutate: (variables) =>
    optimisticUpdateProfile(variables.userId, { bookmark: false }),
  onSuccess: () => {
    native.triggerHapticFeedback(HAPTIC_STYLE.IMPACT_LIGHT);
  },
  onError: (_err, variables, previous) => {
    rollbackProfile(variables.userId, previous);
  },
  onSettled: (_data, _err, variables) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.profile(variables.userId),
    });
    // ブックマーク一覧（マイページの「お気に入り」）を最新化
    queryClient.invalidateQueries({ queryKey: queryKeys.bookmark.list() });
  },
});
